import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
    HomeIcon,
    BuildingOffice2Icon,
    GlobeAsiaAustraliaIcon,
    WrenchScrewdriverIcon,
    PlusIcon,
    BanknotesIcon
} from '@heroicons/react/24/outline';

// Icon mapping
const iconMap = {
    HomeIcon,
    BuildingOffice2Icon,
    GlobeAsiaAustraliaIcon,
    WrenchScrewdriverIcon,
    PlusIcon,
    BanknotesIcon
};

const ProductCard = ({ product, onEdit, onDelete }) => {
    const IconComponent = iconMap[product.icon_name] || HomeIcon;

    // Parse features if it's a string
    const features = typeof product.features === 'string'
        ? JSON.parse(product.features)
        : product.features || [];

    return (
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border border-slate-100">
            {/* Product Header with Image Overlay */}
            <div className="relative h-48 overflow-hidden">
                {/* Background Image or Gradient */}
                {product.image_url ? (
                    <>
                        <img
                            src={product.image_url}
                            alt={product.title}
                            className="w-full h-full object-cover"
                        />
                        {/* Gradient Overlay on Image */}
                        <div className={`absolute inset-0 bg-gradient-to-br from-${product.gradient_from}/80 to-${product.gradient_to}/80`}></div>
                    </>
                ) : (
                    /* Solid Gradient if no image */
                    <div className={`w-full h-full bg-gradient-to-br from-${product.gradient_from} to-${product.gradient_to}`}></div>
                )}

                {/* Icon and Badge Overlay */}
                <div className="absolute inset-0 p-6 flex items-center justify-between">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-white/30 backdrop-blur-md shadow-lg border border-white/20">
                        <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    {/* Display Order Badge */}
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg">
                        <span className="text-sm font-bold text-gray-700">
                            #{product.display_order || 0}
                        </span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-5">
                {/* Active Badge */}
                <div className="mb-3">
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${product.is_active
                        ? 'bg-green-100 text-green-600'
                        : 'bg-slate-100 text-slate-500'
                        }`}>
                        {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-1">
                    {product.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                    {product.description}
                </p>

                {/* Features */}
                {features.length > 0 && (
                    <div className="mb-4 space-y-1">
                        {features.slice(0, 2).map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                                <span className="text-xs text-slate-600">{feature}</span>
                            </div>
                        ))}
                        {features.length > 2 && (
                            <span className="text-xs text-slate-400">+{features.length - 2} more</span>
                        )}
                    </div>
                )}

                {/* Icon Name Tag */}
                <div className="mb-4">
                    <span className="px-2 py-1 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-md">
                        {product.icon_name}
                    </span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t border-slate-100">
                    <button
                        onClick={() => onEdit(product)}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-4 py-2.5 rounded-xl font-medium transition-all duration-200"
                    >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                    </button>
                    <button
                        onClick={() => onDelete(product.id)}
                        className="flex-1 flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2.5 rounded-xl font-medium transition-all duration-200"
                    >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
